import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Avatar,
  IconButton,
  Typography,
  TextField,
  Paper,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CallIcon from "@mui/icons-material/Call";
import VideocamIcon from "@mui/icons-material/Videocam";
import HeadsetMicIcon from "@mui/icons-material/HeadsetMic";
import SendIcon from "@mui/icons-material/Send";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { useDispatch, useSelector } from "react-redux";
import {
  createOrGetThread,
  sendMessage,
  listenToMessages,
  upsertLastMessage,
} from "../../redux/threadsSlice";
import { fetchUserData, listenForAuthChanges } from "../../redux/authSlice";
import { AppDispatch, RootState } from "../../redux/store";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const ThreadsChat: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, allUsers, supportId } = useSelector((s: RootState) => s.auth);
  const { messages } = useSelector((s: RootState) => s.threads);

  const [threadId, setThreadId] = useState<string | null>(null);
  const [text, setText] = useState("");
const [showWarning, setShowWarning] = useState(false);
const [blockedText, setBlockedText] = useState("");

  const flatListRef = useRef<HTMLDivElement>(null);
const [digitBuffer, setDigitBuffer] = useState("");

  // ✅ Detect recipient
  const { userId: paramId } = useParams<{ userId: string }>();
  const location = useLocation();
  const recipientId =
    paramId ||
    (location.state as { userIdB?: string })?.userIdB ||
    supportId ||
    "";

  // ✅ Current user & recipient
  const currentUserId = user?.userId || "";
  const recipient = allUsers?.find((u) => u.userId === recipientId);

  // --- Auth check
  useEffect(() => {
    if (!user) dispatch(listenForAuthChanges());
  }, [dispatch, user]);

  useEffect(() => {
    if (user?.userId) dispatch(fetchUserData());
  }, [dispatch, user?.userId]);

  // --- Start or get thread
  useEffect(() => {
    if (!recipientId || !currentUserId) return;

    dispatch(createOrGetThread({ userIdB: recipientId }))
      .unwrap()
      .then((thread) => {
        setThreadId(thread.id);
        dispatch(listenToMessages({ threadId: thread.id }));
      })
      .catch((err) => console.error("❌ Thread error:", err));
  }, [dispatch, recipientId, currentUserId]);

  // --- Messages
  const threadMessages = threadId ? messages[threadId] || [] : [];

const handleSend = () => {
  if (!text.trim() || !threadId || !currentUserId) return;

  if (violatesContactRules(text)) {
    setBlockedText(text);
    setShowWarning(true);
    setDigitBuffer("");  // important
    return;
  }

  const newMessage = {
    id: Date.now().toString(),
    senderId: currentUserId,
    text,
    type: "text",
    timestamp: Date.now(),
    status: {},
  };

  dispatch(sendMessage({ threadId, text }));
  dispatch(upsertLastMessage({ threadId, message: newMessage }));

  setText("");
  setDigitBuffer(""); // reset on successful send
};



  const formatTime = (timestamp: any) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(timestamp.seconds * 1000);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return "";
    const date =
      typeof timestamp === "number"
        ? new Date(timestamp)
        : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  // --- Group by date
  const groupedMessages: { [date: string]: any[] } = {};
  threadMessages.forEach((msg) => {
    const dateKey = formatDate(msg.timestamp);
    if (!groupedMessages[dateKey]) groupedMessages[dateKey] = [];
    groupedMessages[dateKey].push(msg);
  });

  // --- Scroll to bottom on new message
  useEffect(() => {
    flatListRef.current?.scrollTo({
      top: flatListRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [threadMessages]);


  // Detect phone numbers, email, @username, social media, etc.
const violatesContactRules = (input: string): boolean => {
  if (!input) return false;

  const lowered = input.toLowerCase();

  // 1. Block ANY number instantly
  if (/[0-9]/.test(input)) return true;  

  // 2. Block ANY special characters people use to hide contacts
  if (/[+@#%^*&()/\\\-_=]/.test(input)) return true;

  // 3. Block number words (zero nine seven…)
  const numberWords = [
    "zero","one","two","three","four",
    "five","six","seven","eight","nine",
    "ten","eleven","twelve"
  ];
  if (numberWords.some(w => lowered.includes(w))) return true;

  // 4. Block websites / emails
  if (
    lowered.includes(".com") ||
    lowered.includes(".net") ||
    lowered.includes(".io") ||
    lowered.includes(".co") ||
    lowered.includes(".org")
  ) return true;

  // 5. Block social media terms
  const social = [
    "instagram","facebook","whatsapp",
    "telegram","snapchat","tiktok","ig",
    "fb","wa"
  ];
  if (social.some(s => lowered.includes(s))) return true;

  // 6. Block direct attempts like "call me", "text me"
  const phrases = ["call me","text me","dm me","message me","email me"];
  if (phrases.some(p => lowered.includes(p))) return true;

  return false;
};



  return (


    <Box display="flex" flexDirection="column" height="100vh" bgcolor="#ece5dd">

      {showWarning && (
  <Box
    sx={{
      position: "fixed",
      bottom: 80,
      left: "50%",
      transform: "translateX(-50%)",
      background: "#ff2d55",
      color: "white",
      px: 3,
      py: 2,
      borderRadius: "14px",
      boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
      zIndex: 9999,
      textAlign: "center",
      fontWeight: 600,
      animation: "fadeIn 0.2s ease-out",
    }}
    onClick={() => setShowWarning(false)}
  >
    🚫 Sharing phone numbers, social media or external contacts is not allowed.
    <br />
    <span style={{ fontSize: "0.85rem", opacity: 0.85 }}>
      Remove sensitive info to continue.
    </span>
  </Box>
)}

      {/* --- Header --- */}
      <Box
        display="flex"
        alignItems="center"
        px={2}
        py={1}
        sx={{
          background: "linear-gradient(90deg, #e50914, #bf9b30, #ffffff)",
        }}
      >
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon sx={{ color: "#fff" }} />
        </IconButton>

        {recipient?.profilePicture ? (
          <Avatar src={recipient.profilePicture} sx={{ mx: 1 }} />
        ) : (
          <Avatar sx={{ bgcolor: "#999", mx: 1 }}>
            <PersonOutlineIcon sx={{ color: "#fff" }} />
          </Avatar>
        )}

        <Typography variant="subtitle1" color="#fff" fontWeight={600} flexGrow={1}>
          {recipient?.firstName || "User"}
        </Typography>

        <IconButton>
          <CallIcon sx={{ color: "#fff" }} />
        </IconButton>
        <IconButton>
          <VideocamIcon sx={{ color: "#fff" }} />
        </IconButton>
        <IconButton>
          <HeadsetMicIcon sx={{ color: "#fff" }} />
        </IconButton>
      </Box>

      {/* --- Messages --- */}
      <Box
        ref={flatListRef}
        flexGrow={1}
        overflow="auto"
        p={2}
        display="flex"
        flexDirection="column"
      >
        {Object.keys(groupedMessages).map((dateKey) => (
          <Box key={dateKey}>
            {/* Date Separator */}
            <Box display="flex" justifyContent="center" my={1}>
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 0.5,
                  borderRadius: 2,
                  bgcolor: "#ddd",
                }}
              >
                <Typography variant="caption" color="#555">
                  {dateKey}
                </Typography>
              </Paper>
            </Box>

            {groupedMessages[dateKey].map((msg) => {
              const isMe = msg.senderId === currentUserId;
              return (
                <Box
                  key={msg.id}
                  display="flex"
                  justifyContent={isMe ? "flex-end" : "flex-start"}
                  my={0.5}
                >
                  <Paper
                    sx={{
                      maxWidth: "70%",
                      p: 1.5,
                      borderRadius: 3,
                      bgcolor: isMe ? "#dcf8c6" : "#fff",
                    }}
                  >
                    <Typography variant="body1">{msg.text}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      align="right"
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  </Paper>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      <Divider />

      {/* --- Input --- */}
      <Box display="flex" alignItems="center" p={1} bgcolor="#fff">
        {user?.profilePicture ? (
          <Avatar src={user.profilePicture} sx={{ mr: 1 }} />
        ) : (
          <Avatar sx={{ bgcolor: "#999", mr: 1 }}>
            <PersonOutlineIcon sx={{ color: "#fff" }} />
          </Avatar>
        )}

      <TextField
  fullWidth
  size="small"
  placeholder={text.length === 0 ? `${user?.firstName || "You"}, type a message` : "Type a message..."}
  value={text}
onChange={(e) => {
  const value = e.target.value;

  if (violatesContactRules(value)) {
    setShowWarning(true);
    return; // ❌ do NOT allow the text to be set
  }

  setText(value); // only allow safe text
}}


  sx={{ mr: 1 }}
/>

        <IconButton
          color="primary"
          sx={{ bgcolor: "#075E54", "&:hover": { bgcolor: "#064c44" } }}
          onClick={handleSend}
        >
          <SendIcon sx={{ color: "#fff" }} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ThreadsChat;



const HEADER_HEIGHT = 90; // ✅ taller header for icons

 const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: "flex",
    flex: 1,
    backgroundColor: "#ece5dd",
    flexDirection: "column",
    height: "100vh",
  },

  header: {
    height: 90, // HEADER_HEIGHT
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: "0 10px",
  },

  headerBtn: { padding: 6 },

  avatar: {
    width: 42,
    height: 42,
    borderRadius: "50%",
    margin: "0 8px",
  },

  avatarFallback: {
    backgroundColor: "#999",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },

  headerTitle: { flex: 1, color: "#fff", fontSize: 18, fontWeight: 600 },

  headerActions: { display: "flex", flexDirection: "row", alignItems: "center" },

  dateSeparator: { display: "flex", alignItems: "center", margin: "10px 0" },

  dateText: {
    fontSize: 12,
    color: "#555",
    backgroundColor: "#ddd",
    padding: "4px 12px",
    borderRadius: 12,
  },

  messageRow: { display: "flex", flexDirection: "row", margin: "4px 0" },

  myRow: { justifyContent: "flex-end" },

  theirRow: { justifyContent: "flex-start" },

  messageBubble: {
    maxWidth: "70%",
    borderRadius: 18,
    padding: 10,
  },

  myMessage: {
    backgroundColor: "#dcf8c6",
    marginLeft: "auto",
    borderBottomRightRadius: 2,
  },

  theirMessage: {
    backgroundColor: "#fff",
    marginRight: "auto",
    borderBottomLeftRadius: 2,
  },

  messageText: { fontSize: 15, color: "#111" },

  timestamp: {
    fontSize: 11,
    color: "#555",
    marginTop: 4,
    textAlign: "right",
    display: "block",
  },

  inputRow: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    padding: 8,
    backgroundColor: "#fff",
    borderTop: "1px solid #eee",
  },

  input: {
    flex: 1,
    borderRadius: 20,
    border: "1px solid #ddd",
    padding: "8px 14px",
    fontSize: 15,
    marginRight: 8,
    backgroundColor: "#fff",
  },

  sendBtn: {
    backgroundColor: "#075E54",
    padding: 10,
    borderRadius: 20,
    cursor: "pointer",
  },

  smallAvatar: {
    width: 32,
    height: 32,
    borderRadius: "50%",
    marginRight: 8,
  },
};
